import "../styles/gemSinks.css";
import { Economy, upgradeTargetName } from "@highmarch/app";
import { BalanceData, UpgradeJob, UpgradeTarget } from "@highmarch/domain";
import { priceButtonHtml } from "../components";
import { fmtDuration } from "../format";
import { toast } from "../toast";
import { economyLink } from "./economyLink";
import { gemIcon } from "./econArt";
import { confirmPurchase, econCue, tickPill } from "./purchaseFx";
import { refusalWords } from "./refusalWords";
import { notEnoughGems } from "./getGold";

/**
 * FINISH NOW on the upgrade sheet's running job (MP-07 (a); @highmarch/app Economy.finishNow): gems end the upgrade at once,
 * priced on the speed-up curve (shop.json finishNow). With no speed-up in hand it is the job's gold primary (where
 * "NO SPEED-UPS" stood); with speed-ups, those stay the primary and FINISH NOW is the blue secondary under them. Under
 * VIP's free finish the sheet shows its FREE FINISH instead (the same purchase at 0 gems). It is a hub page the
 * Commander opened, but it still stands down in the quiet after a loss, as "Hire builder" does (the never list).
 */

/** What the sheet shows for a job now: its price and whether the wallet covers it; null: no FINISH NOW here. */
export interface FinishNowOffer { readonly price: number; readonly have: number }

/** The job's FINISH NOW, or null (no economy link — tests, a level —, a free finish, the quiet after a loss). */
export function finishNowOffer(jobId: number, now: number): FinishNowOffer | null {
  const link = economyLink();
  if (!link) return null;
  const eco = new Economy(link.catalog);
  if (!eco.offersAllowed("hub", now)) return null;
  const price = eco.finishNowPrice(jobId, now);
  return typeof price === "number" && price > 0 ? { price, have: eco.wallet.gems } : null;
}

/** A key the sheet's tick compares: the button changes when the price, the wallet or the quiet window does. */
export function finishNowKey(o: FinishNowOffer | null): string { return o ? `${o.price}/${o.have >= o.price ? 1 : 0}` : "-"; }

/** The gold primary (no speed-ups in hand): FINISH · gems. */
export function finishNowPrimaryHtml(o: FinishNowOffer): string {
  return priceButtonHtml({ currency: "gems", amount: o.price, label: "FINISH NOW", short: o.have < o.price, className: "finishnow" });
}

/** The blue secondary under the speed-up: "Finish now · gems". */
export function finishNowSecondaryHtml(o: FinishNowOffer): string {
  return `<div class="jobactions finrow"><button type="button" class="btn blue finishnow sec"><span class="fl">Finish now</span>`
    + `<span class="fp${o.have < o.price ? " short" : ""}">${gemIcon(18)}<b>${o.price}</b></span></button></div>`;
}

/** Whose job it is, in words: "Cannon → level 2" / "Fortress → level 8". */
function jobName(B: BalanceData, job: UpgradeJob): string {
  return `${upgradeTargetName(B, job.target, job.towerIndex)} → level ${job.toLevel}`;
}

/**
 * The tap: the price at this moment, a refusal in words when the gems fall short, else the gem confirm (the one every
 * gem spend in the hub uses), then the purchase at the price shown. Resolves the jobs that completed, or null.
 */
export async function finishNowTap(parent: HTMLElement, B: BalanceData, job: UpgradeJob, now: () => number): Promise<UpgradeJob[] | null> {
  const link = economyLink();
  if (!link) return null;
  const eco = new Economy(link.catalog), price = eco.finishNowPrice(job.id, now());
  if (typeof price === "string") { toast(parent, refusalWords(price)); return null; }
  // VOICE: name the shortfall and the way out ("You need 35 more gems: stars, bosses and Orders pay them")
  if (eco.wallet.gems < price) { void notEnoughGems(parent, price - eco.wallet.gems, parent.querySelector(":scope > .sheet.panel.upgrade"), eco.offersAllowed("hub", now())); return null; }
  const ok = await confirmPurchase(parent, { title: "FINISH NOW", currency: "gems", price, confirm: "FINISH",
    body: `<p><b>${jobName(B, job)}</b> is done at once.</p><p class="fnleft">Saves ${fmtDuration(Math.max(0, job.endAt - now()))} of building.</p>` });
  if (!ok) return null;
  const before = eco.wallet.gems, done = eco.finishNow(job.id, price, now(), "hub");
  if (typeof done === "string") { toast(parent, refusalWords(done)); return null; }
  econCue("buy");
  tickPill("gems", before, eco.wallet.gems);
  return done;
}
